/**
 * What the signed-in person may reach, as the access token states it: ADR 9014 and `bata-reqs`
 * ADR 1001.
 *
 * `RouteBase.requires` in `src/features/manifest.ts` has been declared since the bootstrap and
 * was never read. This module is its reader. The router (`src/app/routes/router.tsx`) uses it to
 * gate a route, and the User Management screen uses it to gate itself.
 *
 * **This is navigation, not security.** G3.1 puts authorisation beneath the UI. The platform
 * resolves the bearer token server-side, and Keycloak's admin API checks its own
 * `realm-management` roles on every call. A person who edits this code to see a screen gets a
 * screen whose every read is refused. The point of the gate is that a person without the grant
 * sees the designed no-permission state (G2 §2.6) and not a wall of 403s.
 *
 * The claims are decoded unverified, for the reason `decodeJwtClaims` in `./session.ts` gives.
 */

import { useMemo } from 'react';

import { decodeJwtPayload, isKeycloakBookkeepingRole, useAuthSessionStore } from './session';

/** Keycloak's built-in client whose roles are the admin API's permissions. */
export const REALM_MANAGEMENT_CLIENT = 'realm-management';

export interface AccessGrants {
  /** The token's `sub`, the Keycloak user id, so a screen can recognise its own account. */
  readonly subject: string | null;
  /** Realm roles, without Keycloak's bookkeeping roles. */
  readonly realmRoles: readonly string[];
  /** `resource_access['realm-management'].roles`, i.e. the admin API permissions. */
  readonly realmManagementRoles: readonly string[];
}

export const NO_GRANTS: AccessGrants = { subject: null, realmRoles: [], realmManagementRoles: [] };

function rolesAt(container: unknown): readonly string[] {
  if (typeof container !== 'object' || container === null) return [];
  const roles = (container as Record<string, unknown>)['roles'];
  return Array.isArray(roles) ? roles.filter((role): role is string => typeof role === 'string') : [];
}

export function readAccessGrants(token: string): AccessGrants {
  const claims = decodeJwtPayload(token);
  if (claims === null) return NO_GRANTS;
  const resourceAccess = claims['resource_access'];
  const realmManagement = typeof resourceAccess === 'object' && resourceAccess !== null
    ? (resourceAccess as Record<string, unknown>)[REALM_MANAGEMENT_CLIENT]
    : undefined;
  const sub = claims['sub'];
  return {
    subject: typeof sub === 'string' && sub !== '' ? sub : null,
    realmRoles: rolesAt(claims['realm_access']).filter((role) => !isKeycloakBookkeepingRole(role)),
    realmManagementRoles: rolesAt(realmManagement),
  };
}

/**
 * Whether `roles` satisfies a route's `requires`. **Every** listed role is needed. An absent or
 * empty `requires` needs nothing, which is the state of every chapter route today.
 */
export function hasGrants(requires: readonly string[] | undefined, roles: readonly string[]): boolean {
  if (requires === undefined) return true;
  return requires.every((role) => roles.includes(role));
}

/** The members of `required` that `held` lacks, in `required`'s order. */
export function missingGrants(required: readonly string[], held: readonly string[]): readonly string[] {
  return required.filter((role) => !held.includes(role));
}

/**
 * The signed-in person's grants, or `NO_GRANTS` with no token. That includes the e2e bypass
 * build, which has a session and no token, so a gated route under e2e renders its
 * no-permission state. That state is what the axe audit then covers.
 */
export function useAccessGrants(): AccessGrants {
  const token = useAuthSessionStore((s) => s.accessToken);
  return useMemo(() => (token === null ? NO_GRANTS : readAccessGrants(token)), [token]);
}
